import { BeforeInstallPromptEvent, PwaInstallationContextValue, PwaInstallationProviderProps } from './types';
import { getPwaInstallationScenario, isRunningAsInstalledPwa } from './pwaCapabilities';
import { PwaInstallationContext } from './context';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const PwaInstallationProvider = (props: PwaInstallationProviderProps) => {
  const {
    children
  } = props;
  const nativePromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [hasNativePrompt, setHasNativePrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(() => isRunningAsInstalledPwa(navigator, window));

  useEffect(() => {
    const onAppInstalled = (): void => {
      nativePromptRef.current = null;
      setHasNativePrompt(false);
      setIsInstalled(true);
    };
    const onBeforeInstallPrompt = (event: Event): void => {
      if (isInstalled) return;

      const beforeInstallPromptEvent = event as BeforeInstallPromptEvent;

      beforeInstallPromptEvent.preventDefault();
      nativePromptRef.current = beforeInstallPromptEvent;
      setHasNativePrompt(true);
    };

    window.addEventListener('appinstalled', onAppInstalled);
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    return () => {
      window.removeEventListener('appinstalled', onAppInstalled);
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    };
  }, [isInstalled]);

  const promptNativeInstallation = useCallback(async () => {
    const nativePrompt = nativePromptRef.current;

    if (!nativePrompt) return 'unavailable';

    nativePromptRef.current = null;
    setHasNativePrompt(false);
    await nativePrompt.prompt();

    const choice = await nativePrompt.userChoice;

    return choice.outcome;
  }, []);

  const scenario = getPwaInstallationScenario({
    hasNativePrompt,
    isInstalled,
    navigatorValue: navigator
  });
  const value = useMemo<PwaInstallationContextValue>(() => ({
    promptNativeInstallation,
    scenario
  }), [promptNativeInstallation, scenario]);

  return (
    <PwaInstallationContext.Provider value={value}>
      {children}
    </PwaInstallationContext.Provider>
  );
};

export default PwaInstallationProvider;